// Mobile Optimized Input Components
// Provides touch-friendly inputs with proper keyboard types for iOS and Android

import React from 'react';

/**
 * Number input optimized for mobile
 * Shows numeric keyboard on iOS and Android
 */
export const MobileNumberInput = ({ 
    value, 
    onChange, 
    placeholder = "0",
    label = "",
    min = 0,
    max = 999999999,
    step = 1,
    prefix = "",
    suffix = "",
    className = "",
    ...props 
}) => {
    return (
        <div className="w-full">
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                </label>
            )}
            <div className="relative">
                {prefix && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">{prefix}</span>
                    </div>
                )}
                <input
                    type="number"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    step={step}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        prefix ? 'pl-8' : ''
                    } ${suffix ? 'pr-12' : ''} ${className}`}
                    style={{
                        fontSize: '16px', // Prevents iOS auto-zoom
                        minHeight: '48px',
                        touchAction: 'manipulation',
                        WebkitAppearance: 'none',
                        MozAppearance: 'textfield'
                    }}
                    {...props}
                />
                {suffix && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">{suffix}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Currency input optimized for mobile
 */
export const MobileCurrencyInput = ({ value, onChange, label, ...props }) => {
    return (
        <MobileNumberInput
            value={value}
            onChange={onChange}
            label={label}
            prefix="$"
            step={1000}
            {...props}
        />
    );
};

/**
 * Percentage input optimized for mobile
 */
export const MobilePercentageInput = ({ value, onChange, label, ...props }) => {
    return (
        <MobileNumberInput
            value={value}
            onChange={onChange}
            label={label}
            suffix="%"
            min={0}
            max={100}
            step={0.1}
            {...props}
        />
    );
};

/**
 * Age input optimized for mobile
 */
export const MobileAgeInput = ({ value, onChange, label, ...props }) => {
    return (
        <MobileNumberInput
            value={value}
            onChange={onChange}
            label={label}
            placeholder="Age"
            min={18}
            max={100}
            step={1}
            {...props}
        />
    );
};

/**
 * Email input optimized for mobile
 */
export const MobileEmailInput = ({ value, onChange, label, className = "", ...props }) => {
    return (
        <div className="w-full">
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                </label>
            )}
            <input
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                value={value}
                onChange={onChange}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
                style={{
                    fontSize: '16px',
                    minHeight: '48px',
                    touchAction: 'manipulation'
                }}
                {...props}
            />
        </div>
    );
};

/**
 * Select dropdown optimized for mobile
 */
export const MobileSelect = ({ value, onChange, options = [], label, className = "", ...props }) => {
    return (
        <div className="w-full">
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                </label>
            )}
            <select
                value={value}
                onChange={onChange}
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${className}`}
                style={{
                    fontSize: '16px',
                    minHeight: '48px',
                    touchAction: 'manipulation',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em',
                    paddingRight: '2.5rem'
                }}
                {...props}
            >
                {options.map((option) => (
                    <option 
                        key={option.value} 
                        value={option.value}
                        disabled={option.disabled}
                        style={option.disabled ? { color: '#9ca3af', fontStyle: 'italic' } : {}}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

/**
 * Checkbox optimized for mobile
 */
export const MobileCheckbox = ({ checked, onChange, label, className = "", ...props }) => {
    return (
        <label className="flex items-center cursor-pointer touch-friendly">
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                style={{
                    width: '24px',
                    height: '24px',
                    minWidth: '24px',
                    minHeight: '24px',
                    touchAction: 'manipulation'
                }}
                {...props}
            />
            {label && (
                <span className="ml-3 text-sm text-gray-700">{label}</span>
            )}
        </label>
    );
};

/**
 * Button optimized for mobile
 */
export const MobileButton = ({ 
    children, 
    onClick, 
    variant = 'primary', 
    size = 'medium',
    fullWidth = false,
    disabled = false,
    className = "",
    ...props 
}) => {
    const variants = {
        primary: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800',
        secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300 active:bg-gray-400',
        outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 active:bg-blue-100',
        danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800'
    };

    const sizes = {
        small: 'px-4 py-2 text-sm min-h-[40px]',
        medium: 'px-6 py-3 text-base min-h-[48px]',
        large: 'px-8 py-4 text-lg min-h-[56px]'
    };

    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`
                ${variants[variant]} 
                ${sizes[size]} 
                ${fullWidth ? 'w-full' : ''}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                rounded-lg font-medium transition-all shadow-md active:shadow-sm
                ${className}
            `}
            style={{
                fontSize: '16px',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent'
            }}
            {...props}
        >
            {children}
        </button>
    );
};

export default {
    MobileNumberInput,
    MobileCurrencyInput,
    MobilePercentageInput,
    MobileAgeInput,
    MobileEmailInput,
    MobileSelect,
    MobileCheckbox,
    MobileButton
};

